import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle, FileCheck2, Loader2, MapPin, Upload } from "lucide-react";
import type { DispatchRecord } from "@/types";

const day = (d?: string | null) => (d ? new Date(`${d.slice(0, 10)}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-");
const when = (d: string) => new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
export const onTheRoad = (d: DispatchRecord) => d.dispatch_status === "DISPATCHED" || d.dispatch_status === "IN_TRANSIT";

/** E-way bill, expected delivery, tracking updates and proof of delivery of one dispatch */
export function DispatchPaperwork({ d }: { d: DispatchRecord }) {
  const track = d.tracking || [];
  if (d.dispatch_status === "CANCELLED") return null;
  return (
    <div className="text-xs space-y-1.5">
      {d.eway_bill_number ? (
        <p className={d.eway_expired ? "text-rose-600 font-medium" : "text-muted-foreground"}>
          E-way bill <span className="font-mono">{d.eway_bill_number}</span>
          {d.eway_valid_until ? ` · valid until ${day(d.eway_valid_until)}` : ""}
          {d.eway_expired ? " · EXPIRED: extend it before the goods are stopped" : ""}
        </p>
      ) : d.eway_required && onTheRoad(d) ? (
        <p className="text-amber-700 dark:text-amber-400 inline-flex items-center gap-1 font-medium">
          <AlertTriangle className="size-3.5" /> Goods worth over ₹50,000 need an e-way bill. Add it.
        </p>
      ) : null}
      {onTheRoad(d) && d.expected_delivery && <p className="text-muted-foreground">Expected by {day(d.expected_delivery)}</p>}
      {d.received_by && <p className="text-muted-foreground">Received by {d.received_by}</p>}
      {d.has_pod && (
        <p>
          <a href={`/api/SalesOrder/PodFile/${d.id}`} target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">
            <FileCheck2 className="size-3.5" /> Proof of delivery: {d.pod_file_name}
          </a>
        </p>
      )}
      {track.length > 0 && (
        <ol className="border-l-2 border-border ml-1 pl-3 space-y-1">
          {[...track].reverse().map((t) => (
            <li key={t.id} className="relative">
              <span className="absolute -left-[17px] top-1 size-2 rounded-full bg-primary" />
              <span className="text-muted-foreground">{when(t.event_at)}</span>
              {t.location && <span className="ml-2 font-medium inline-flex items-center gap-0.5"><MapPin className="size-3" />{t.location}</span>}
              {t.note && <span className="ml-2">{t.note}</span>}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function useSave(onSaved: () => void, onClose: () => void) {
  const { success, error } = useToast();
  const [saving, setSaving] = useState(false);
  const run = async (fn: () => Promise<unknown>, done: string) => {
    setSaving(true);
    try {
      await fn();
      success(done, "");
      onSaved();
      onClose();
    } catch (err: any) {
      error("Could not save", err.message || "Please try again");
    } finally {
      setSaving(false);
    }
  };
  return { saving, run, error };
}

/** Where the consignment is now, and when it is expected; the first update moves it to In transit */
export function TrackingDialog({ dispatch, onClose, onSaved }: { dispatch: DispatchRecord | null; onClose: () => void; onSaved: () => void }) {
  const { saving, run, error } = useSave(onSaved, onClose);
  const [f, setF] = useState({ location: "", note: "", expected: "" });
  useEffect(() => {
    if (dispatch) setF({ location: "", note: "", expected: dispatch.expected_delivery || "" });
  }, [dispatch?.id]);
  const submit = () => {
    if (!dispatch) return;
    if (!f.location.trim() && !f.note.trim() && f.expected === (dispatch.expected_delivery || "")) return error("Validation Error", "Say where it is, add a note, or change the expected date");
    void run(() => api.post(`/api/SalesOrder/Tracking/${dispatch.id}`, { location: f.location.trim() || undefined, note: f.note.trim() || undefined, expected_delivery: f.expected || undefined }), "Tracking updated");
  };
  return (
    <Dialog open={!!dispatch} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Tracking · {dispatch?.dispatch_number}</DialogTitle>
          <DialogDescription>Add what the transporter or driver told you. The first update marks the goods as in transit.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div className="space-y-1.5"><Label>Where is it?</Label><Input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} placeholder="Surat hub, Vadodara toll…" autoFocus /></div>
          <div className="space-y-1.5"><Label>Note</Label><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Loaded, crossed the toll, held at check post…" /></div>
          <div className="space-y-1.5"><Label>Expected by</Label><Input type="date" value={f.expected} onChange={(e) => setF({ ...f, expected: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving}>{saving && <Loader2 className="size-4 animate-spin mr-1.5" />}Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** The 12-digit e-way bill number from the GST portal, and how long it is valid */
export function EwayDialog({ dispatch, onClose, onSaved }: { dispatch: DispatchRecord | null; onClose: () => void; onSaved: () => void }) {
  const { saving, run, error } = useSave(onSaved, onClose);
  const [f, setF] = useState({ number: "", date: "", until: "" });
  useEffect(() => {
    if (dispatch) setF({ number: dispatch.eway_bill_number || "", date: dispatch.eway_bill_date?.slice(0, 10) || "", until: dispatch.eway_valid_until?.slice(0, 10) || "" });
  }, [dispatch?.id]);
  const submit = () => {
    if (!dispatch) return;
    if (!/^\d{12}$/.test(f.number.trim())) return error("Validation Error", "The e-way bill number is 12 digits");
    void run(() => api.put(`/api/SalesOrder/EwayBill/${dispatch.id}`, { eway_bill_number: f.number.trim(), eway_bill_date: f.date || undefined, eway_valid_until: f.until || undefined }), "E-way bill saved");
  };
  return (
    <Dialog open={!!dispatch} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>E-way bill · {dispatch?.dispatch_number}</DialogTitle>
          <DialogDescription>Generated on the GST portal. Needed for goods worth over ₹50,000 ({dispatch?.value != null ? `this consignment is about ₹${Math.round(dispatch.value).toLocaleString("en-IN")}` : "check the value"}).</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div className="space-y-1.5"><Label>E-way bill number *</Label><Input inputMode="numeric" maxLength={12} value={f.number} onChange={(e) => setF({ ...f, number: e.target.value.replace(/\D/g, "") })} className="font-mono" autoFocus /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Generated on</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Valid until</Label><Input type="date" value={f.until} onChange={(e) => setF({ ...f, until: e.target.value })} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving}>{saving && <Loader2 className="size-4 animate-spin mr-1.5" />}Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Uploads a PDF or photo of the challan signed by the buyer's store */
export async function uploadPod(dispatchId: string, file: File, receivedBy?: string) {
  const fd = new FormData();
  fd.append("file", file);
  if (receivedBy?.trim()) fd.append("received_by", receivedBy.trim());
  await api.post(`/api/SalesOrder/PodUpload/${dispatchId}`, fd);
}

export function PodDialog({ dispatch, onClose, onSaved }: { dispatch: DispatchRecord | null; onClose: () => void; onSaved: () => void }) {
  const { saving, run, error } = useSave(onSaved, onClose);
  const [file, setFile] = useState<File | null>(null);
  const [by, setBy] = useState("");
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (dispatch) {
      setFile(null);
      setBy(dispatch.received_by || "");
    }
  }, [dispatch?.id]);
  const submit = () => {
    if (!dispatch) return;
    if (!file) return error("Validation Error", "Choose the signed challan (PDF or photo)");
    void run(() => uploadPod(dispatch.id, file, by), "Proof of delivery filed");
  };
  return (
    <Dialog open={!!dispatch} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Proof of delivery · {dispatch?.dispatch_number}</DialogTitle>
          <DialogDescription>The challan signed and stamped by the buyer, as a PDF or a photo. {dispatch?.has_pod ? "Filing another replaces the current one." : ""}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <input ref={ref} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          <Button type="button" variant="outline" className="w-full justify-start gap-2 h-10" onClick={() => ref.current?.click()}>
            <Upload className="size-4" /> {file ? file.name : "Choose a file…"}
          </Button>
          <div className="space-y-1.5"><Label>Received by</Label><Input value={by} onChange={(e) => setBy(e.target.value)} placeholder="Name of the person who signed" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !file}>{saving && <Loader2 className="size-4 animate-spin mr-1.5" />}File it</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
